import React, { useState, useEffect, useRef } from "react";
import emailjs from "@emailjs/browser";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { gsap } from "gsap";

/* ========= FAQ Data ========= */
const faqItems = [
  {
    question: "What programs does Akash Institute of Pharmacy offer?",
    answer:
      "Akash Institute of Pharmacy currently offers the Bachelor of Pharmacy (B.Pharm).",
  },
  {
    question: "What are the eligibility criteria for the B.Pharm program?",
    answer:
      "Candidates must have passed 10+2 with Physics and Chemistry as compulsory subjects.",
  },
];

/* ========= Animation Variants ========= */
const fadeInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.85 } },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.85, delay: 0.15 } },
};

const formFieldVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const fieldVariant = {
  hidden: { opacity: 0, y: 22 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

/* ========= Popup Component ========= */
function StatusPopup({ type, onClose }) {
  const isSuccess = type === "success";

  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.85, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.85, y: 30 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#ffffff",
            borderRadius: "20px",
            padding: "48px 40px 40px",
            maxWidth: "420px",
            width: "100%",
            textAlign: "center",
            boxShadow: "0 24px 80px rgba(0,0,0,0.18)",
            position: "relative",
          }}
        >
          {/* Icon */}
          <div
            style={{
              width: "72px",
              height: "72px",
              borderRadius: "50%",
              background: isSuccess ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            {isSuccess ? (
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                <circle cx="18" cy="18" r="18" fill="#22c55e" />
                <path d="M10 18.5l5.5 5.5 10.5-11" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                <circle cx="18" cy="18" r="18" fill="#ef4444" />
                <path d="M12 12l12 12M24 12L12 24" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            )}
          </div>

          {/* Title */}
          <h3 style={{ fontSize: "20px", fontWeight: 700, color: "#1a1a1a", marginBottom: "10px" }}>
            {isSuccess ? "Enquiry Submitted!" : "Submission Failed"}
          </h3>

          {/* Message */}
          <p style={{ fontSize: "14px", color: "#6b7280", lineHeight: 1.7, marginBottom: "28px" }}>
            {isSuccess
              ? "Thank you! Your enquiry has been received. Our admissions team will get back to you within 24 hours."
              : "Something went wrong while sending your enquiry. Please try again or contact us directly."}
          </p>

          {/* Auto-close progress bar */}
          <div style={{ height: "3px", background: "#f3f4f6", borderRadius: "2px", overflow: "hidden", marginBottom: "20px" }}>
            <motion.div
              initial={{ width: "100%" }}
              animate={{ width: "0%" }}
              transition={{ duration: 5, ease: "linear" }}
              style={{ height: "100%", background: isSuccess ? "#22c55e" : "#ef4444", borderRadius: "2px" }}
            />
          </div>

          {/* Buttons */}
          <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
            <button
              onClick={onClose}
              style={{
                padding: "10px 28px",
                borderRadius: "30px",
                border: "none",
                background: isSuccess ? "#1E5AA8" : "#ef4444",
                color: "#fff",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {isSuccess ? "Done" : "Try Again"}
            </button>
            {!isSuccess && (
              <a
                href="tel:+919513597575"
                style={{
                  padding: "10px 28px",
                  borderRadius: "30px",
                  border: "1.5px solid #e5e7eb",
                  background: "transparent",
                  color: "#374151",
                  fontSize: "14px",
                  fontWeight: 600,
                  textDecoration: "none",
                  display: "inline-block",
                }}
              >
                Call Us
              </a>
            )}
          </div>

          {/* Close X */}
          <button
            onClick={onClose}
            style={{
              position: "absolute",
              top: "16px",
              right: "16px",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "#9ca3af",
              fontSize: "20px",
              lineHeight: 1,
              padding: "4px",
            }}
          >
            ×
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/* ========= Main Component ========= */
function Frequently() {
  const [activeIndex, setActiveIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [popupType, setPopupType] = useState(null); // "success" | "error" | null
  const [errors, setErrors] = useState({});

  const formRef = useRef(null);
  const submitRef = useRef(null);

  const { ref: leftRef, inView: leftInView } = useInView({ threshold: 0.2, triggerOnce: true });
  const { ref: rightRef, inView: rightInView } = useInView({ threshold: 0.2, triggerOnce: true });

  const handleToggle = (index) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  /* Button hover animation */
  const handleBtnEnter = () => gsap.to(submitRef.current, { scale: 1.06, duration: 0.3 });
  const handleBtnLeave = () => gsap.to(submitRef.current, { scale: 1, duration: 0.3 });

  /* ---- Validation ---- */
  const validate = (data) => {
    const errs = {};

    if (!data.user_name.trim()) {
      errs.user_name = "Full name is required.";
    } else if (data.user_name.trim().length < 2) {
      errs.user_name = "Name must be at least 2 characters.";
    }

    if (!data.user_email.trim()) {
      errs.user_email = "Email address is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.user_email)) {
      errs.user_email = "Please enter a valid email address.";
    }

    if (!data.user_phone.trim()) {
      errs.user_phone = "Phone number is required.";
    } else if (!/^[6-9]\d{9}$/.test(data.user_phone.replace(/\s/g, ""))) {
      errs.user_phone = "Enter a valid 10-digit Indian mobile number.";
    }

    if (!data.course) {
      errs.course = "Please select a course.";
    }

    return errs;
  };

  /* ---- Submit ---- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const form = formRef.current;
    const data = {
      user_name: form.user_name.value,
      user_email: form.user_email.value,
      user_phone: form.user_phone.value,
      course: form.course.value,
      message: form.message?.value || "",
    };

    const errs = validate(data);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setLoading(true);
    try {
      await emailjs.sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        form,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      setPopupType("success");
      form.reset();
      setErrors({});
    } catch (error) {
      console.error(error);
      setPopupType("error");
    }
    setLoading(false);
  };

  /* ---- Inline error style ---- */
  const errStyle = {
    color: "#ef4444",
    fontSize: "12px",
    marginTop: "4px",
    textAlign: "left",
    display: "block",
  };

  return (
    <>
      {/* ===== Popup ===== */}
      {popupType && <StatusPopup type={popupType} onClose={() => setPopupType(null)} />}

      <section className="faq-area pt-120 pb-120 p-relative fix">
        <div className="container">
          <div className="row justify-content-center align-items-center">

            {/* ===== LEFT: FAQ ===== */}
            <div className="col-lg-7" ref={leftRef}>
              <motion.div
                className="section-title"
                variants={fadeInLeft}
                initial="hidden"
                animate={leftInView ? "visible" : "hidden"}
              >
                <h2>Frequently Asked Questions</h2>
              </motion.div>

              {faqItems.map((item, index) => (
                <div
                  key={index}
                  className="faq-item"
                  style={{ marginBottom: "12px", borderRadius: "8px", overflow: "hidden", border: "1px solid #e5e7eb" }}
                >
                  <button
                    onClick={() => handleToggle(index)}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "16px 20px",
                      background: activeIndex === index ? "#1E5AA8" : "#fff",
                      color: activeIndex === index ? "#fff" : "#1a1a1a",
                      border: "none",
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: "15px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {item.question}
                    <span style={{ fontSize: "20px", fontWeight: 700, flexShrink: 0, marginLeft: "12px" }}>
                      {activeIndex === index ? "−" : "+"}
                    </span>
                  </button>
                  {activeIndex === index && (
                    <motion.div
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25 }}
                      style={{ padding: "14px 20px", background: "#f9fafb", fontSize: "14px", color: "#374151", lineHeight: 1.75 }}
                    >
                      {item.answer}
                    </motion.div>
                  )}
                </div>
              ))}
            </div>

            {/* ===== RIGHT: Form ===== */}
            <div className="col-lg-5" ref={rightRef}>
              <motion.div
                className="contact-bg02"
                variants={fadeInRight}
                initial="hidden"
                animate={rightInView ? "visible" : "hidden"}
              >
                <motion.div className="section-title">
                  <h2>Make An Contact</h2>
                </motion.div>

                <motion.form
                  ref={formRef}
                  onSubmit={handleSubmit}
                  className="contact-form mt-30"
                  variants={formFieldVariants}
                  initial="hidden"
                  animate={rightInView ? "visible" : "hidden"}
                  noValidate
                >
                  <div className="row">

                    {/* Name */}
                    <motion.div className="col-lg-12" variants={fieldVariant}>
                      <div className="contact-field mb-20">
                        <input
                          type="text"
                          name="user_name"
                          placeholder="Full Name"
                          style={errors.user_name ? { borderColor: "#ef4444" } : {}}
                          onChange={() => setErrors((p) => ({ ...p, user_name: "" }))}
                        />
                        {errors.user_name && <span style={errStyle}>⚠ {errors.user_name}</span>}
                      </div>
                    </motion.div>

                    {/* Email */}
                    <motion.div className="col-lg-12" variants={fieldVariant}>
                      <div className="contact-field mb-20">
                        <input
                          type="email"
                          name="user_email"
                          placeholder="Email Address"
                          style={errors.user_email ? { borderColor: "#ef4444" } : {}}
                          onChange={() => setErrors((p) => ({ ...p, user_email: "" }))}
                        />
                        {errors.user_email && <span style={errStyle}>⚠ {errors.user_email}</span>}
                      </div>
                    </motion.div>

                    {/* Phone */}
                    <motion.div className="col-lg-12" variants={fieldVariant}>
                      <div className="contact-field mb-20">
                        <input
                          type="text"
                          name="user_phone"
                          placeholder="Phone Number"
                          maxLength={10}
                          style={errors.user_phone ? { borderColor: "#ef4444" } : {}}
                          onChange={() => setErrors((p) => ({ ...p, user_phone: "" }))}
                        />
                        {errors.user_phone && <span style={errStyle}>⚠ {errors.user_phone}</span>}
                      </div>
                    </motion.div>

                    {/* Course — custom select */}
                    <motion.div className="col-lg-12" variants={fieldVariant}>
                      <div className="contact-field mb-20">
                        <select
                          name="course"
                          className="faq-custom-select"
                          style={errors.course ? { borderColor: "#ef4444" } : {}}
                          onChange={() => setErrors((p) => ({ ...p, course: "" }))}
                        >
                          <option value="">Select Course</option>
                          <option value="Bachelor of Pharmacy (B.Pharm)">
                            Bachelor of Pharmacy (B.Pharm)
                          </option>
                        </select>
                        {errors.course && <span style={errStyle}>⚠ {errors.course}</span>}
                      </div>
                    </motion.div>

                    {/* Message */}
                    <motion.div className="col-lg-12" variants={fieldVariant}>
                      <div className="contact-field mb-30">
                        <textarea
                          name="message"
                          rows="6"
                          placeholder="Please share your enquiry, admission interest, or any questions about the B.Pharm program."
                        />
                      </div>

                      <input type="hidden" name="website" value="Akash Institute of Pharmacy" />

                      <motion.div className="slider-btn">
                        <button
                          ref={submitRef}
                          className="btn ss-btn"
                          type="submit"
                          disabled={loading}
                          onMouseEnter={handleBtnEnter}
                          onMouseLeave={handleBtnLeave}
                          style={{ opacity: loading ? 0.75 : 1 }}
                        >
                          {loading ? (
                            <span style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }}>
                              <svg width="16" height="16" viewBox="0 0 16 16" style={{ animation: "faq-spin 0.8s linear infinite" }}>
                                <circle cx="8" cy="8" r="6" stroke="rgba(255,255,255,0.3)" strokeWidth="2" fill="none" />
                                <path d="M8 2a6 6 0 0 1 6 6" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" />
                              </svg>
                              Sending...
                            </span>
                          ) : "Submit Now"}
                        </button>
                      </motion.div>
                    </motion.div>

                  </div>
                </motion.form>
              </motion.div>
            </div>

          </div>
        </div>
      </section>

      {/* ===== Styles ===== */}
      <style>{`
        @keyframes faq-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* ---- Autofill override ---- */
        .contact-field input:-webkit-autofill,
        .contact-field input:-webkit-autofill:hover,
        .contact-field input:-webkit-autofill:focus,
        .contact-field input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0px 1000px #ffffff inset !important;
          box-shadow: 0 0 0px 1000px #ffffff inset !important;
          -webkit-text-fill-color: #333333 !important;
          background-color: #ffffff !important;
          transition: background-color 99999s ease-in-out 0s;
        }

        /* ---- White focus border ---- */
        .contact-field input:focus,
        .contact-field textarea:focus {
          border-color: #ffffff !important;
          outline: none;
        }

        /* ---- Custom select ---- */
        .contact-field .faq-custom-select {
          width: 100%;
          height: 50px;
          padding: 0 40px 0 14px;
          color: #9e9e9e;
          font-size: 16px;
          font-weight: 400;
          border: 1px solid rgba(255,255,255,0.6);
          border-radius: 6px;
          background-color: #fff;
          appearance: none;
          -webkit-appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%237f7873' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'%3E%3C/path%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: calc(100% - 12px) center;
          background-size: 20px;
          cursor: pointer;
          transition: border-color 0.25s ease;
        }

        .contact-field .faq-custom-select:focus {
          border-color: #ffffff !important;
          outline: none;
        }

        .contact-field .faq-custom-select {
          font-size: 14px;
        }

        .contact-field .faq-custom-select option {
          font-size: 14px;
          color: #000 !important;
        }

        .contact-field .faq-custom-select option[value=""] {
          color: #000 !important;
        }
      `}</style>
    </>
  );
}

export default Frequently;
